import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Alert,
    Box,
    Typography,
    Chip,
    Checkbox,
    InputAdornment,
    Tooltip,
} from "@mui/material";
import {
    Add,
    Search,
} from "@mui/icons-material";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import debounce from "lodash.debounce";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AssignUsersDialog = ({ open, onClose, roleId, roleName }) => {
    const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);

    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState(""); // for the search input
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [alert, setAlert] = useState({ type: "", msg: "" });
    const [showWarning, setShowWarning] = useState(false);

    // Fetch assigned users
    const refreshAssignedUsers = useCallback(async () => {
        if (!roleId) return;
        setLoadingAssigned(true);
        try {
            const res = await fetch(`${BASE_URL}/api/roles/${boId}/${roleId}/users`);
            const data = await res.json();
            setAssignedUsers(Array.isArray(data) ? data : data.data || []);
        } catch (err) {
            console.error("Failed to fetch assigned users:", err);
            setAlert({ type: "error", msg: "Failed to load assigned users" });
        } finally {
            setLoadingAssigned(false);
        }
    }, [roleId]);

    useEffect(() => {
        if (open) {
            refreshAssignedUsers();
            setSearchResults([]);
            setSelectedUsers([]);
            setSearchQuery(""); // Clear search input
        }
    }, [open, refreshAssignedUsers]);
    const assignedUsersRef = useRef([]);
    useEffect(() => {
        assignedUsersRef.current = assignedUsers;
    }, [assignedUsers]);
    // Auto-clear alert after 3 seconds
    useEffect(() => {
        if (!alert.msg) return;

        const timer = setTimeout(() => {
            setAlert({ type: "", msg: "" });
        }, 6000); // 6 seconds

        return () => clearTimeout(timer);
    }, [alert.msg]);
    // Search users
    const handleSearch = useCallback(
        debounce(async (query) => {
            if (!query?.trim()) {
                setSearchResults([]);
                return;
            }
            setLoadingSearch(true);
            try {
                const res = await fetch(`${BASE_URL}/api/users/${boId}?search=${query}`);
                const data = await res.json();
                const users = data?.data || [];
                const assignedIds = new Set(assignedUsersRef.current.map(u => u.user_id));
                const filtered = users.filter(u => !assignedIds.has(u.user_id));
                setSearchResults(filtered);
            } catch (err) {
                console.error("Search failed:", err);
            } finally {
                setLoadingSearch(false);
            }
        }, 300),
        [boId]
    );
    const handleAssignUser = async (user, confirmed = false) => {
        if (!confirmed) {
            setShowWarning(true);
            return;
        }
        try {
            const response = await fetch(
                `${BASE_URL}/api/roles/${boId}/${roleId}/users/${user.user_id}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ empId, boId }),
                }
            );

            if (!response.ok) {
                let errorMsg = `Failed to assign user (status ${response.status})`;
                try {
                    const errData = await response.json();
                    if (errData?.message) errorMsg = errData.message;
                } catch {
                    const errText = await response.text();
                    if (errText) errorMsg = errText;
                }
                throw new Error(errorMsg);
            }

            setAlert({
                type: "success",
                msg: `Added ${user.name} to ${roleName}`,
            });
            refreshAssignedUsers();

            // Clear search input and remove from results
            setSearchQuery("");
            setSearchResults("");

        } catch (err) {
            console.error("Assign failed:", err);
            setAlert({
                type: "error",
                msg: err.message || "Failed to add user",
            });
        }
    };

    const handleAssignSelectedUsers = async () => {
        setShowWarning(true);
    };

    const handleConfirmAssign = async () => {
        setShowWarning(false);
        for (const user of selectedUsers) {
            await handleAssignUser(user, true);
        }
        setSelectedUsers([]);
    };


    const handleRemove = async (user) => {
        try {
            const response = await fetch(
                `${BASE_URL}/api/roles/${boId}/${roleId}/users/${user.user_id}`,
                {
                    method: "DELETE",
                }
            );

            if (!response.ok) {
                let errorMsg = `Failed to remove user (status ${response.status})`;
                try {
                    const errData = await response.json();
                    if (errData?.message) errorMsg = errData.message;
                } catch {
                    const errText = await response.text();
                    if (errText) errorMsg = errText;
                }
                throw new Error(errorMsg);
            }

            setAlert({
                type: "success",
                msg: `Removed ${user.first_name} ${user.last_name} from ${roleName}`,
            });
            refreshAssignedUsers();
        } catch (err) {
            console.error("Unassign failed:", err);
            setAlert({
                type: "error",
                msg: err.message || "Failed to remove user",
            });
        }
    };

    const handleSelectUser = (user, isSelected) => {
        setSelectedUsers(prev =>
            isSelected ? [...prev, user] : prev.filter(u => u.user_id !== user.user_id)
        );
    };

    return (
        <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Role Users in - <span>{roleName || ""}</span>
            </DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: "70vh" }}>
                {/* Alert */}
                {alert.msg && (
                    <Alert severity={alert.type} onClose={() => setAlert({ type: "", msg: "" })}>
                        {alert.msg}
                    </Alert>
                )}

                {/* Group-first warning */}
                <Alert severity="warning" sx={{ mb: 0 }}>
                    Direct user assignment should only be used as an exception. The recommended approach is to assign users to a group, then assign the group to this role.
                </Alert>

                {/* Search Users Section */}
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                        <Typography variant="subtitle1">
                            Search & Add Users
                        </Typography>
                        {/* Search Users */}
                        <TextField
                            size="small"
                            sx={{ width: "250px", height: 34 }}
                            placeholder="Search by name or email"
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                handleSearch(e.target.value);
                            }}
                            InputProps={{
                                sx: {
                                    height: 36, // Reduce overall input height
                                    paddingRight: 0,
                                },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    {/* Selected Users Preview */}
                    {selectedUsers.length > 0 && (
                        <Box sx={{ mt: 1, mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {selectedUsers.map((user) => (
                                <Chip
                                    key={user.user_id}
                                    label={user.name}
                                    onDelete={() => handleSelectUser(user, false)}
                                    color="primary"
                                />
                            ))}
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    fontWeight: "bold",
                                    borderRadius: "8px",
                                    px: 2.5,
                                    height: 36,
                                    backgroundColor: "#2DB0E4",
                                }}
                                startIcon={<Add />}
                                onClick={handleAssignSelectedUsers}
                            >
                                Add Selected
                            </Button>
                        </Box>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Roles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searchResults.map((user) => (
                                    <TableRow key={user.user_id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUsers.some(u => u.user_id === user.user_id)}
                                                onChange={(e) => handleSelectUser(user, e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            {user.roles && user.roles.length > 0
                                                ? user.roles.map((role, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={role}
                                                        size="small"
                                                        color="primary"
                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                    />
                                                ))
                                                : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                {/* Assigned Users Section */}
                <Box sx={{ flex: 1, overflowY: "auto", maxHeight: 400, position: "relative" }}>
                    <Typography variant="subtitle1" sx={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "background.paper",
                        zIndex: 2,
                        py: 1, // optional padding
                    }}>

                        Assigned Users
                    </Typography>
                    {loadingAssigned ? (
                        <CircularProgress size={24} />
                    ) : assignedUsers.length === 0 ? (
                        <p>No users assigned.</p>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" sx={{ position: "sticky", top: 32, backgroundColor: "background.paper", zIndex: 1 }}>Action</TableCell>
                                    <TableCell sx={{ position: "sticky", top: 32, backgroundColor: "background.paper", zIndex: 1 }}>Name</TableCell>
                                    <TableCell sx={{ position: "sticky", top: 32, backgroundColor: "background.paper", zIndex: 1 }}>Email</TableCell>
                                    <TableCell sx={{ position: "sticky", top: 32, backgroundColor: "background.paper", zIndex: 1 }}>Assignment</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignedUsers.map((user) => (
                                    <TableRow key={user.user_id}>
                                        <TableCell align="left">
                                            {user.assignment_type === "direct" ? (
                                                <IconButton color="error" onClick={() => handleRemove(user)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            ) : (
                                                <Tooltip title="Remove user from their group to revoke this role">
                                                    <span>
                                                        <IconButton disabled>
                                                            <DeleteIcon style={{ opacity: 0.3 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{user.first_name} {user.last_name}</TableCell>
                                        <TableCell>{user.email}</TableCell>
                                        <TableCell>
                                            {user.assignment_type === "group"
                                                ? <Chip label="via group" size="small" color="success" variant="outlined" />
                                                : <Chip label="direct" size="small" color="warning" variant="outlined" />
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)}>Close</Button>
            </DialogActions>

            {/* Confirm direct assignment warning dialog */}
            <Dialog open={showWarning} onClose={() => setShowWarning(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Direct User Assignment</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        You are assigning users directly to this role. This should only be done as an exception. The recommended approach is to assign users to a group and assign the group to this role.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowWarning(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmAssign} variant="contained" color="warning">Proceed Anyway</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default AssignUsersDialog;
